import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { QmRecord, QmCrudStatus } from '../../api/qmCrud';
import {
  QM_ATTACHMENT_COMMENT_PREFIX,
  QM_CREATION_ATTACHMENT_COMMENT_PREFIX_MULTI,
  QM_CREATION_ATTACHMENT_COMMENT_PREFIX_SINGLE
} from '../../common/constants';

type HistoryItem = {
  id: string;
  type: 'STATUS' | 'CHANGE' | 'ACTION';
  time: string;
  title: string;
  detail: string;
  by: string;
  isCreationAttachment?: boolean;
};

type HistoryGroup = {
  id: string;
  time: string;
  items: HistoryItem[];
};

type ReasonOption = {
  code: string;
  label: string;
};

type ClosureOption = {
  code: string;
  label: string;
};

type QmHistoryTimelineProps = {
  selectedQm: QmRecord;
  reasonOptions: ReasonOption[];
  closureOptions: ClosureOption[];
  sectionCardSx: Record<string, unknown>;
  getLocationLabel: (itemLocation: string | null | undefined) => string;
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string;
};

const CHANGE_FIELD_LABELS: Record<string, string> = {
  reasonCode: 'Reason',
  poNumber: 'PO Number',
  poLine: 'PO Line',
  poLineId: 'PO Line ID',
  itemId: 'Item ID',
  sscc: 'SSCC',
  asn: 'ASN',
  supplierName: 'Supplier',
  contactName: 'Contact',
  itemLocation: 'Item Location',
  packageCount: 'Package Count',
  packageType: 'Package Type',
  description: 'Description',
  closureCode: 'Closure Code',
  closureNotes: 'Closure Notes',
  otherInformation: 'Additional Details',
  type: 'Type',
  supplierCode: 'Supplier Code',
  qmNumber: 'QM Number',
  targetType: 'Target Type',
  tripReference: 'Trip',
  manifestId: 'Manifest',
  siteInboundSscc: 'Site Inbound SSCC',
  siteInboundAsn: 'Site Inbound ASN',
  siteInboundPoNumber: 'Site Inbound PO Number',
  siteInboundPoLineId: 'Site Inbound PO Line ID',
  siteInboundItemId: 'Site Inbound Item ID',
  siteInboundItemLocationId: 'Site Inbound Item Location',
  siteInboundAsnCount: 'Selected ASNs',
  siteInboundAsnLineCount: 'Selected ASN Lines'
};

const CHANGE_FIELD_LABELS_LOWERCASE: Record<string, string> = Object.fromEntries(
  Object.entries(CHANGE_FIELD_LABELS).map(([key, label]) => [key.toLowerCase(), label])
);

const HIDDEN_HISTORY_FIELDS = new Set(['tripid']);
const ATTACHMENT_CREATION_WINDOW_SECONDS = 120;
const FIELD_CHANGE_CREATION_WINDOW_SECONDS = 5;

function getChangeFieldLabel(fieldName: string): string {
  const normalized = String(fieldName || '').trim();
  if (!normalized) return 'Field';

  return CHANGE_FIELD_LABELS[normalized] || CHANGE_FIELD_LABELS_LOWERCASE[normalized.toLowerCase()] || normalized;
}

function formatAsnLineDetail(line: QmRecord['poLines'][number]): string {
  const payload = line.linePayload || {};
  const sourcePartId = String(payload.sourcePartId || payload.originalPartId || payload.original_part_id || '').trim();
  const description = String(payload.description || '').trim();
  const received = line.receivedQuantity ?? (payload.quantityReceived as number | null | undefined);
  const receivedValue = received === null || received === undefined ? '' : String(received);
  const parts = [sourcePartId, description, receivedValue ? `Received: ${receivedValue}` : ''].filter(Boolean);
  return parts.join(' - ') || '-';
}

function classifyChangeAction(oldValue: string, newValue: string): 'CREATE' | 'UPDATE' | 'CLEAR' {
  const normalize = (value: string) =>
    value
      .trim()
      .replace(/^['"]+|['"]+$/g, '')
      .toLowerCase();

  const oldNormalized = normalize(oldValue);
  const newNormalized = normalize(newValue);

  const oldIsEmpty = oldNormalized === 'null' || oldNormalized === 'undefined' || oldNormalized === '';
  const newIsEmpty = newNormalized === 'null' || newNormalized === 'undefined' || newNormalized === '';

  if (oldIsEmpty && !newIsEmpty) return 'CREATE';
  if (!oldIsEmpty && newIsEmpty) return 'CLEAR';
  return 'UPDATE';
}

function classifyChangeType(
  fieldName: string,
  oldValue: string,
  newValue: string
): 'CREATE' | 'UPDATE' | 'CLEAR' | 'CLOSURE' {
  const baseType = classifyChangeAction(oldValue, newValue);
  const normalizedField = fieldName.trim().toLowerCase();
  const isClosureField = normalizedField === 'closurecode' || normalizedField === 'closurenotes';

  if (baseType === 'CREATE' && isClosureField) return 'CLOSURE';
  return baseType;
}

function parseChangeDetail(detail: string): { oldRaw: string; newRaw: string } {
  const parts = detail.split(/\s*(?:->|→|=>)\s*/);
  if (parts.length >= 2) {
    return {
      oldRaw: parts[0] || '',
      newRaw: parts.slice(1).join(' -> ') || ''
    };
  }

  return {
    oldRaw: '',
    newRaw: detail
  };
}

function normalizeHistoryRawValue(rawValue: string): string {
  // Defensive normalization: some persisted history values can arrive wrapped in quotes,
  // which would break label lookups (for example itemLocation code-to-label mapping).
  return String(rawValue || '')
    .trim()
    .replace(/^['"]+|['"]+$/g, '');
}

type HistoryTone = {
  accent: string;
  chipBg: string;
  chipText: string;
  chipBorder: string;
};

type SummaryCardType = 'CREATION' | 'CLOSURE' | 'UPDATE' | 'ACTION' | 'STATUS' | null;

function resolveSummaryCardType(params: {
  hasNonCreationActionItems: boolean;
  hasCreationStatusItems: boolean;
  hasCreationActionItems: boolean;
  hasClosureEntries: boolean;
  hasGroupedCreationChanges: boolean;
  hasGroupedUpdateChanges: boolean;
  hasStatusItems: boolean;
}): SummaryCardType {
  const {
    hasNonCreationActionItems,
    hasCreationStatusItems,
    hasCreationActionItems,
    hasClosureEntries,
    hasGroupedCreationChanges,
    hasGroupedUpdateChanges,
    hasStatusItems
  } = params;

  if (!hasNonCreationActionItems && (hasCreationStatusItems || hasCreationActionItems || hasGroupedCreationChanges)) {
    return 'CREATION';
  }
  if (!hasNonCreationActionItems && hasClosureEntries) return 'CLOSURE';
  if (hasGroupedUpdateChanges) return 'UPDATE';
  if (hasNonCreationActionItems) return 'ACTION';
  if (hasStatusItems) return 'STATUS';
  return null;
}

function getHistoryTone(label: string): HistoryTone {
  switch (label) {
    case 'STATUS':
      return {
        accent: '#3b82f6',
        chipBg: '#f1f7ff',
        chipText: '#1e40af',
        chipBorder: '#c7dbff'
      };
    case 'CREATE':
    case 'CREATION':
      return {
        accent: '#0d9488',
        chipBg: '#f2fffd',
        chipText: '#0f766e',
        chipBorder: '#b8efe8'
      };
    case 'CLOSURE':
      return {
        accent: '#d97706',
        chipBg: '#fffbf2',
        chipText: '#92400e',
        chipBorder: '#f7deb2'
      };
    case 'ACTION':
      return {
        accent: '#4f46e5',
        chipBg: '#eef2ff',
        chipText: '#3730a3',
        chipBorder: '#c7d2fe'
      };
    case 'CLEAR':
      return {
        accent: '#ea580c',
        chipBg: '#fff7f1',
        chipText: '#9a3412',
        chipBorder: '#f9d3bf'
      };
    case 'UPDATE':
    default:
      return {
        accent: '#db2777',
        chipBg: '#fdf2f8',
        chipText: '#9d174d',
        chipBorder: '#fbcfe8'
      };
  }
}

function toActorDisplay(value: string | null | undefined): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return normalized || '-';
}

function parseChangeFieldName(item: HistoryItem): string {
  if (item.type !== 'CHANGE') return '';
  return item.title.replace(/^Field changed:\s*/i, '').trim();
}

function isAttachmentAction(detail: string): boolean {
  const normalized = String(detail || '')
    .trim()
    .toLowerCase();

  const attachmentPrefixes = [
    QM_ATTACHMENT_COMMENT_PREFIX,
    QM_CREATION_ATTACHMENT_COMMENT_PREFIX_SINGLE,
    QM_CREATION_ATTACHMENT_COMMENT_PREFIX_MULTI
  ];

  return attachmentPrefixes.findIndex((prefix) => normalized.startsWith(prefix.toLowerCase())) !== -1;
}

function isLegacyCreationAttachmentAction(detail: string): boolean {
  const normalized = String(detail || '')
    .trim()
    .toLowerCase();

  return (
    normalized.startsWith(QM_CREATION_ATTACHMENT_COMMENT_PREFIX_SINGLE.toLowerCase()) ||
    normalized.startsWith(QM_CREATION_ATTACHMENT_COMMENT_PREFIX_MULTI.toLowerCase())
  );
}

function isWithinCreationWindow(
  eventTime: string,
  qmCreatedAt: string,
  windowSeconds = ATTACHMENT_CREATION_WINDOW_SECONDS
): boolean {
  const eventAt = dayjs(eventTime);
  const createdAt = dayjs(qmCreatedAt);

  if (!eventAt.isValid() || !createdAt.isValid()) return false;
  if (eventAt.isBefore(createdAt)) return false;

  return eventAt.diff(createdAt, 'second') <= windowSeconds;
}

function classifyChangeTypeForHistoryItem(
  item: HistoryItem,
  qmCreatedAt: string
): 'CREATE' | 'UPDATE' | 'CLEAR' | 'CLOSURE' {
  const fieldName = parseChangeFieldName(item);
  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  const actionType = classifyChangeType(fieldName, oldRaw, newRaw);

  if (
    actionType === 'CREATE' &&
    !isWithinCreationWindow(item.time, qmCreatedAt, FIELD_CHANGE_CREATION_WINDOW_SECONDS)
  ) {
    return 'UPDATE';
  }

  return actionType;
}

function formatHistoryDetail(item: HistoryItem, qmCreatedAt: string) {
  if (item.type !== 'CHANGE') return item.detail;

  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  const actionType = classifyChangeTypeForHistoryItem(item, qmCreatedAt);

  if (actionType === 'CREATE') return `Created with ${newRaw}`;
  if (actionType === 'CLOSURE') return `Closure set to ${newRaw}`;
  if (actionType === 'CLEAR') return `Cleared from ${oldRaw}`;
  return `${oldRaw} -> ${newRaw}`;
}

function getHistoryChip(item: HistoryItem, qmCreatedAt: string): { label: string } {
  if (item.type === 'STATUS') return { label: 'STATUS' };
  if (item.type === 'ACTION') {
    return { label: item.isCreationAttachment ? 'CREATION' : 'ACTION' };
  }

  const actionType = classifyChangeTypeForHistoryItem(item, qmCreatedAt);
  if (actionType === 'CREATE') return { label: 'CREATE' };
  if (actionType === 'CLOSURE') return { label: 'CLOSURE' };
  if (actionType === 'CLEAR') return { label: 'CLEAR' };
  return { label: 'UPDATE' };
}

function isCreateChangeItem(item: HistoryItem, qmCreatedAt: string): boolean {
  if (item.type !== 'CHANGE') return false;
  return classifyChangeTypeForHistoryItem(item, qmCreatedAt) === 'CREATE';
}

function isClosureChangeItem(item: HistoryItem): boolean {
  if (item.type !== 'CHANGE') return false;
  const fieldName = parseChangeFieldName(item);
  const { oldRaw, newRaw } = parseChangeDetail(item.detail);
  return classifyChangeType(fieldName, oldRaw, newRaw) === 'CLOSURE';
}

function toDisplayValue(
  fieldName: string,
  rawValue: string,
  reasonOptions: ReasonOption[],
  closureOptions: ClosureOption[],
  selectedQm: QmRecord,
  getLocationLabel: (itemLocation: string | null | undefined) => string,
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string
): string {
  const normalized = normalizeHistoryRawValue(rawValue);
  if (!normalized || normalized.toLowerCase() === 'null') return '-';

  if (fieldName === 'status') {
    return formatStatusLabel(normalized);
  }

  if (fieldName === 'type') {
    if (selectedQm.type === normalized && selectedQm.typeLabel) return selectedQm.typeLabel;
    if (normalized === 'SITE_INBOUND') return 'Site Inbound';
    if (normalized === 'INBOUND') return 'Inbound';
    return normalized
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  if (fieldName === 'reasonCode') {
    const reason = reasonOptions.find((item) => item.code === normalized);
    if (reason?.label) return reason.label;
    if (selectedQm.reasonCode === normalized && selectedQm.reasonLabel) return selectedQm.reasonLabel;
    return normalized;
  }

  if (fieldName === 'closureCode') {
    const closure = closureOptions.find((item) => item.code === normalized);
    if (closure?.label) return closure.label;
    if (selectedQm.closureCode === normalized && selectedQm.closureLabel) return selectedQm.closureLabel;
    return normalized;
  }

  if (fieldName === 'itemLocation') {
    return getLocationLabel(normalized);
  }

  if (fieldName === 'siteInboundItemLocationId') {
    return selectedQm.itemLocation ? selectedQm.itemLocation : normalized;
  }

  if (fieldName === 'targetType') {
    return normalized
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  if (fieldName === 'siteInboundAsnLineCount') {
    const asnLineDetails = (selectedQm.poLines || []).map(formatAsnLineDetail).filter((value) => value !== '-');
    if (asnLineDetails.length > 0) return asnLineDetails.join('\n');
    return normalized;
  }

  // For PO/package fields, history cards intentionally show the full current child-row set
  // instead of only mirrored scalar values stored in legacy columns.
  if (fieldName === 'poLine') {
    const allPoLines = (selectedQm.poLines || [])
      .map((line) => String(line.poLine || '').trim())
      .filter((value) => value.length > 0);
    if (allPoLines.length > 0) return allPoLines.join('\n');
    return normalized;
  }

  if (fieldName === 'poLineId') {
    const allPoLineIds = (selectedQm.poLines || [])
      .map((line) => String(line.poLineId || '').trim())
      .filter((value) => value.length > 0);
    if (allPoLineIds.length > 0) return allPoLineIds.join(', ');
    return normalized;
  }

  if (fieldName === 'itemId') {
    const allItemIds = (selectedQm.poLines || [])
      .map((line) => String(line.itemId || '').trim())
      .filter((value) => value.length > 0);
    if (allItemIds.length > 0) return allItemIds.join(', ');
    return normalized;
  }

  if (fieldName === 'packageCount') {
    const packageRows = selectedQm.packages || [];
    if (packageRows.length > 0) return String(packageRows.length);
    return normalized;
  }

  if (fieldName === 'packageType') {
    const allPackages = (selectedQm.packages || [])
      .sort((a, b) => a.packageNumber - b.packageNumber)
      .map((pkg) => {
        const typeValue = String(pkg.packageType || '').trim() || '-';
        return `#${pkg.packageNumber}: ${typeValue}`;
      });
    if (allPackages.length > 0) return allPackages.join(', ');
    return normalized;
  }

  return normalized;
}

function renderHistoryItemCard(item: HistoryItem, qmCreatedAt: string) {
  const chip = getHistoryChip(item, qmCreatedAt);
  const tone = getHistoryTone(chip.label);
  const title =
    item.type === 'CHANGE' ? `Field changed: ${getChangeFieldLabel(parseChangeFieldName(item))}` : item.title;

  return (
    <Paper
      key={item.id}
      variant="outlined"
      sx={{
        minWidth: 0,
        p: 0.85,
        borderRadius: 1.25,
        borderLeft: '3px solid',
        borderLeftColor: tone.accent,
        bgcolor: 'background.default'
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.2 }}>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Chip
            size="small"
            label={chip.label}
            variant="outlined"
            sx={{
              bgcolor: tone.chipBg,
              color: tone.chipText,
              borderColor: tone.chipBorder,
              fontWeight: 700
            }}
          />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{title}</Typography>
        </Stack>
        <Stack spacing={0.1} alignItems="flex-end">
          <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
            {dayjs(item.time).format('DD/MM/YYYY HH:mm:ss')}
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{toActorDisplay(item.by)}</Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{
          fontSize: '0.7rem',
          color: 'text.secondary',
          lineHeight: 1.3,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word'
        }}>
        {formatHistoryDetail(item, qmCreatedAt)}
      </Typography>
    </Paper>
  );
}

export default function QmHistoryTimeline({
  selectedQm,
  reasonOptions,
  closureOptions,
  sectionCardSx,
  getLocationLabel,
  formatStatusLabel
}: QmHistoryTimelineProps) {
  const historyItems: HistoryItem[] = useMemo(() => {
    const statusItems: HistoryItem[] = selectedQm.statusHistory.map((entry) => ({
      id: `status-${entry.id}`,
      type: 'STATUS',
      time: entry.changedAt,
      title: `Status ${formatStatusLabel(entry.fromStatus)} -> ${formatStatusLabel(entry.toStatus)}`,
      detail: entry.note || 'No note',
      by: entry.changedBy
    }));

    const changeItems: HistoryItem[] = selectedQm.changeHistory
      .filter(
        (entry) =>
          String(entry.fieldName || '')
            .trim()
            .toLowerCase() !== 'status'
      )
      .map((entry) => ({
        id: `change-${entry.id}`,
        type: 'CHANGE',
        time: entry.changedAt,
        title: `Field changed: ${entry.fieldName}`,
        detail: `${entry.oldValue ?? 'null'} -> ${entry.newValue ?? 'null'}`,
        by: entry.changedBy
      }));

    const actionItems: HistoryItem[] = (selectedQm.comments || []).map((entry) => {
      const isAttachment = isAttachmentAction(entry.body);
      const isCreationAttachment =
        isLegacyCreationAttachmentAction(entry.body) ||
        (isAttachment && isWithinCreationWindow(entry.createdAt, selectedQm.createdAt));

      return {
        id: `action-${entry.id}`,
        type: 'ACTION',
        time: entry.createdAt,
        title: isCreationAttachment ? 'Creation' : 'Action',
        detail: entry.body,
        by: entry.createdBy,
        isCreationAttachment
      };
    });

    return [...statusItems, ...changeItems, ...actionItems].sort(
      (a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf()
    );
  }, [formatStatusLabel, selectedQm]);

  const historyGroups: HistoryGroup[] = useMemo(() => {
    const groups: HistoryGroup[] = [];
    const groupIndexByTime = new Map<string, number>();

    for (const item of historyItems) {
      const isAttachmentActionItem = item.type === 'ACTION' && isAttachmentAction(item.detail);
      const groupKey = isAttachmentActionItem
        ? `attachment-${dayjs(item.time).format('YYYY-MM-DD HH:mm')}-${toActorDisplay(item.by)}`
        : `exact-${dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')}`;
      const existingIndex = groupIndexByTime.get(groupKey);

      if (existingIndex !== undefined) {
        groups[existingIndex].items.push(item);
        continue;
      }

      groupIndexByTime.set(groupKey, groups.length);
      groups.push({
        id: `group-${groupKey}`,
        time: item.time,
        items: [item]
      });
    }

    return groups;
  }, [historyItems]);

  const historyCardCount = useMemo(() => {
    return historyGroups.reduce((count, group) => {
      const parsedCreateChanges = group.items.map((item) => {
        if (item.type !== 'CHANGE') return null;

        const fieldName = parseChangeFieldName(item);
        const actionType = classifyChangeTypeForHistoryItem(item, selectedQm.createdAt);

        return {
          id: item.id,
          fieldName,
          actionType
        };
      });

      const creationEntries = parsedCreateChanges
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .filter((entry) => entry.actionType === 'CREATE');
      const closureEntries = parsedCreateChanges
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .filter((entry) => entry.actionType === 'CLOSURE');
      const updateEntries = parsedCreateChanges
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .filter(
          (entry) => entry.actionType === 'UPDATE' || entry.actionType === 'CLEAR' || entry.actionType === 'CREATE'
        );
      const creationIds = new Set(creationEntries.map((entry) => entry.id));
      const closureIds = new Set(closureEntries.map((entry) => entry.id));
      const updateIds = new Set(updateEntries.map((entry) => entry.id));
      const actionEntries = group.items.filter((item) => item.type === 'ACTION');
      const creationActionIds = new Set(
        actionEntries.filter((item) => item.isCreationAttachment).map((item) => item.id)
      );
      const statusEntries = group.items.filter((item) => item.type === 'STATUS');
      const hasNonCreationActionItems = actionEntries.some((item) => !item.isCreationAttachment);
      const hasCreationActionItems = creationActionIds.size > 0;
      const hasStatusItems = statusEntries.length > 0;
      const hasCreationStatusItems = statusEntries.some((item) => /status\s+n\/a\s*->/i.test(item.title));
      const hasOnlyChangeItems = group.items.length > 0 && group.items.every((item) => item.type === 'CHANGE');
      const hasGroupedCreationChanges =
        hasOnlyChangeItems && creationEntries.length > 0 && creationEntries.length === updateEntries.length;
      const hasGroupedUpdateChanges = hasOnlyChangeItems && updateEntries.length > 0 && !hasGroupedCreationChanges;
      const summaryCardType = resolveSummaryCardType({
        hasNonCreationActionItems,
        hasCreationStatusItems,
        hasCreationActionItems,
        hasClosureEntries: closureEntries.length > 0,
        hasGroupedCreationChanges,
        hasGroupedUpdateChanges,
        hasStatusItems
      });

      const itemsToRender =
        summaryCardType === 'CREATION'
          ? group.items.filter((item) => !creationIds.has(item.id) && !creationActionIds.has(item.id))
          : summaryCardType === 'CLOSURE'
            ? group.items.filter((item) => !closureIds.has(item.id))
            : summaryCardType === 'UPDATE'
              ? group.items.filter((item) => !updateIds.has(item.id))
              : summaryCardType === 'ACTION'
                ? group.items.filter((item) => item.type !== 'ACTION' || creationActionIds.has(item.id))
                : summaryCardType === 'STATUS'
                  ? group.items.filter((item) => item.type !== 'STATUS')
                  : group.items;

      if (summaryCardType) {
        return count + 1 + itemsToRender.length;
      }

      return count + itemsToRender.length;
    }, 0);
  }, [historyGroups, selectedQm.createdAt]);

  return (
    <Paper variant="outlined" sx={sectionCardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">History</Typography>
        <Chip size="small" label={`${historyCardCount} entries`} variant="outlined" />
      </Stack>
      <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto', overflowX: 'hidden' }}>
        {historyGroups.map((group) => {
          const uniqueActors = Array.from(
            new Set(group.items.map((item) => toActorDisplay(item.by)).filter((actor) => actor !== '-'))
          );
          const singleActor = uniqueActors.length === 1 ? uniqueActors[0] : null;

          const parsedCreateChanges = group.items.map((item) => {
            if (item.type !== 'CHANGE') return null;

            const { oldRaw, newRaw } = parseChangeDetail(item.detail);
            const fieldName = parseChangeFieldName(item);
            const actionType = classifyChangeTypeForHistoryItem(item, selectedQm.createdAt);

            return {
              id: item.id,
              fieldName,
              oldRaw,
              newRaw,
              actionType
            };
          });

          const creationEntries = parsedCreateChanges
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
            .filter((entry) => entry.actionType === 'CREATE');
          const closureEntries = parsedCreateChanges
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
            .filter((entry) => entry.actionType === 'CLOSURE');
          const actionEntries = group.items
            .filter((item) => item.type === 'ACTION')
            .map((item) => ({
              id: item.id,
              label: 'Action Comment',
              value: item.detail,
              isCreationAttachment: !!item.isCreationAttachment
            }));
          const creationActionEntries = actionEntries.filter((entry) => entry.isCreationAttachment);
          const nonCreationActionEntries = actionEntries.filter((entry) => !entry.isCreationAttachment);
          const statusEntries = group.items
            .filter((item) => item.type === 'STATUS')
            .map((item) => ({
              id: item.id,
              label: 'Status Change',
              isCreationStatus: /status\s+n\/a\s*->/i.test(item.title),
              value:
                item.detail && item.detail !== 'No note'
                  ? `${item.title.replace(/^Status\s*/i, '')} | ${item.detail}`
                  : item.title.replace(/^Status\s*/i, '')
            }));
          const updateEntries = parsedCreateChanges
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
            .filter(
              (entry) => entry.actionType === 'UPDATE' || entry.actionType === 'CLEAR' || entry.actionType === 'CREATE'
            );
          const hasNonCreationActionItems = nonCreationActionEntries.length > 0;
          const hasCreationActionItems = creationActionEntries.length > 0;
          const hasStatusItems = statusEntries.length > 0;
          const hasCreationStatusItems = statusEntries.some((entry) => entry.isCreationStatus);
          const hasOnlyChangeItems = group.items.length > 0 && group.items.every((item) => item.type === 'CHANGE');
          const hasGroupedCreationChanges =
            hasOnlyChangeItems && creationEntries.length > 0 && creationEntries.length === updateEntries.length;
          const hasGroupedUpdateChanges = hasOnlyChangeItems && updateEntries.length > 0 && !hasGroupedCreationChanges;
          const summaryCardType = resolveSummaryCardType({
            hasNonCreationActionItems,
            hasCreationStatusItems,
            hasCreationActionItems,
            hasClosureEntries: closureEntries.length > 0,
            hasGroupedCreationChanges,
            hasGroupedUpdateChanges,
            hasStatusItems
          });
          const summaryEntries =
            summaryCardType === 'CREATION'
              ? creationEntries
              : summaryCardType === 'CLOSURE'
                ? closureEntries
                : updateEntries;
          const itemsToRender =
            summaryCardType === 'CREATION'
              ? group.items.filter(
                  (item) =>
                    !isCreateChangeItem(item, selectedQm.createdAt) &&
                    !(item.type === 'ACTION' && item.isCreationAttachment)
                )
              : summaryCardType === 'CLOSURE'
                ? group.items.filter((item) => !isClosureChangeItem(item))
                : summaryCardType === 'UPDATE'
                  ? group.items.filter((item) => item.type !== 'CHANGE')
                  : summaryCardType === 'ACTION'
                    ? group.items.filter(
                        (item) => item.type !== 'ACTION' || (item.type === 'ACTION' && item.isCreationAttachment)
                      )
                    : summaryCardType === 'STATUS'
                      ? group.items.filter((item) => item.type !== 'STATUS')
                      : group.items;

          if (summaryCardType) {
            const orderedSummaryEntries = summaryEntries
              .filter((entry) => !HIDDEN_HISTORY_FIELDS.has(entry.fieldName.toLowerCase()))
              .sort((a, b) => {
                const priority = (fieldName: string) => {
                  if (fieldName === 'closureCode') return 1;
                  if (fieldName === 'closureNotes') return 2;
                  if (fieldName === 'reasonCode') return 1;
                  if (fieldName === 'poNumber') return 2;
                  if (fieldName === 'poLine') return 3;
                  if (fieldName === 'poLineId') return 4;
                  if (fieldName === 'itemId') return 5;
                  if (fieldName === 'itemLocation') return 6;
                  return 50;
                };
                return priority(a.fieldName) - priority(b.fieldName);
              });
            const summaryDisplayEntries =
              summaryCardType === 'ACTION'
                ? nonCreationActionEntries
                : summaryCardType === 'STATUS'
                  ? statusEntries
                  : summaryCardType === 'CREATION'
                    ? [
                        ...orderedSummaryEntries.map((entry) => ({
                          id: entry.id,
                          label: getChangeFieldLabel(entry.fieldName),
                          value: toDisplayValue(
                            entry.fieldName,
                            entry.newRaw,
                            reasonOptions,
                            closureOptions,
                            selectedQm,
                            getLocationLabel,
                            formatStatusLabel
                          )
                        })),
                        ...creationActionEntries.map((entry) => ({
                          id: entry.id,
                          label: 'Attachment',
                          value: entry.value
                        }))
                      ]
                    : orderedSummaryEntries.map((entry) => ({
                        id: entry.id,
                        label: getChangeFieldLabel(entry.fieldName),
                        value: toDisplayValue(
                          entry.fieldName,
                          entry.newRaw,
                          reasonOptions,
                          closureOptions,
                          selectedQm,
                          getLocationLabel,
                          formatStatusLabel
                        )
                      }));
            const summaryTone = getHistoryTone(summaryCardType);

            return (
              <Stack key={group.id} spacing={0.8}>
                <Paper
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    p: 1.1,
                    borderRadius: 1.5,
                    borderLeft: '3px solid',
                    borderLeftColor: summaryTone.accent,
                    bgcolor: 'background.default',
                    overflowX: 'hidden'
                  }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Chip
                      size="small"
                      label={summaryCardType}
                      variant="outlined"
                      sx={{
                        bgcolor: summaryTone.chipBg,
                        color: summaryTone.chipText,
                        borderColor: summaryTone.chipBorder,
                        fontWeight: 700
                      }}
                    />
                    <Stack spacing={0.1} alignItems="flex-end">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                        {dayjs(group.time).format('DD/MM/YYYY HH:mm:ss')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                        {singleActor || 'multiple users'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 0.6,
                      minWidth: 0
                    }}>
                    {summaryDisplayEntries.map((entry) => {
                      return (
                        <Box
                          key={entry.id}
                          sx={{
                            minWidth: 0,
                            px: 0.8,
                            py: 0.55,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}>
                          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontWeight: 600 }}>
                            {entry.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              color: 'text.primary',
                              lineHeight: 1.3,
                              whiteSpace: 'pre-line',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word'
                            }}>
                            {entry.value || '-'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
                {itemsToRender.length > 0 ? (
                  <Stack spacing={0.65}>
                    {itemsToRender.map((item) => renderHistoryItemCard(item, selectedQm.createdAt))}
                  </Stack>
                ) : null}
              </Stack>
            );
          }

          return (
            <Stack key={group.id} spacing={0.65}>
              {itemsToRender.map((item) => renderHistoryItemCard(item, selectedQm.createdAt))}
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
